import { MicrosoftService } from '@api/api/alfred-integrations';
import { Alert, AlertType } from '@design/components/alert/Alert';
import { useMicrosoftServices } from '@terminal/react-query-hooks/alfred/useMicrosoftServices';
import { useAuthStore } from '@terminal/stores/use-auth/store/authStore';
import { useTranslation } from 'react-i18next';
import { useMicrosoftConnect } from '../connection-apps-panel/hooks/useMicrosoftConnect';

export const TOAST_ID = 'connection-toast';

export type ConnectionToastAction = 'connect' | 'disconnect' | 'notConnected';
export type ConnectionToastStatus = 'success' | 'error';

export interface ConnectionToastProps {
  action: ConnectionToastAction;
  name: string;
  status: ConnectionToastStatus;
  removeConversation?: boolean;
  serviceName?: MicrosoftService;
  message?: string;
}

export const ConnectionToast = ({
  action,
  name,
  status,
  removeConversation = true,
  serviceName,
  message,
}: ConnectionToastProps) => {
  const { t } = useTranslation(['chat']);

  const userId = useAuthStore(state => state.user_id);
  const {
    data: services = [],
    isLoading,
    refetch,
  } = useMicrosoftServices(userId);

  const { connectMicrosoft } = useMicrosoftConnect({
    services,
    isLoadingServices: isLoading,
    refetchServices: refetch,
  });

  const title = (() => {
    if (action === 'notConnected') {
      return t('chat:microsoftItem.notConnectedTitle', { name });
    }

    if (action === 'connect') {
      return status === 'success'
        ? t('chat:connectionToast.connectSuccessWithName', { name })
        : t('chat:connectionToast.connectFailedWithName', { name });
    }

    if (status === 'success') {
      return removeConversation
        ? t(
            'chat:connectionToast.disconnectAndRemoveConversationSuccessWithName',

            { name }
          )
        : t('chat:connectionToast.disconnectSuccessWithName', { name });
    }

    return t('chat:connectionToast.disconnectFailedWithName', { name });
  })();

  const type = status === 'success' ? AlertType.Success : AlertType.Error;

  const getMessage = () => {
    if (action === 'notConnected') {
      return t('chat:microsoftItem.notConnectedDescription', { name });
    }

    return message ?? '';
  };

  const primaryBtn = () => {
    if (action === 'notConnected' && serviceName) {
      return {
        label: t('chat:microsoftItem.reconnectButton', { name }),
        onClick: () => connectMicrosoft(serviceName),
      };
    }

    return undefined;
  };

  return (
    <Alert
      type={type}
      title={title}
      message={getMessage()}
      primaryBtn={primaryBtn()}
      data-testid="connection-toast"
    />
  );
};
